/**
 * Prices on the branded line sheet (KP-4545), with the labels a buyer reads
 * ("Wholesale", "Retail") instead of "WSP:" and "RRP:".
 *
 * catalog-service already strips a hidden price from the public payload
 * (KP-3861). This applies the same visibility rule again in the view, so a
 * figure that ever slipped through still never prints, and one price is never
 * shown in place of the other (the KP-3727 leak).
 */

const LOCALE = 'en-GB'

/** Which prices the brand shows; the public defaults when unset. */
export function visiblePriceKinds(priceVisibility) {
  const v = priceVisibility || {}
  return { wholesale: v.wholesale === true, retail: v.retail !== false }
}

/** "€80", "US$312.83": the currency's own symbol, ".00" dropped. */
export function formatMoney(amount, currency) {
  const whole = Number.isInteger(amount)
  const digits = whole ? 0 : 2
  const code = String(currency || '').toUpperCase()
  if (/^[A-Z]{3}$/.test(code)) {
    try {
      return new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount)
    } catch (e) {
      // An unknown code: fall through to the plain number.
    }
  }
  const number = amount.toFixed(digits)
  return code ? `${number} ${code}` : number
}

function lowestPositive(values) {
  const positives = values.filter(v => typeof v === 'number' && Number.isFinite(v) && v > 0)
  return positives.length ? Math.min(...positives) : null
}

function wholesaleAmount(product) {
  const items = Array.isArray(product.items) ? product.items : []
  return lowestPositive(items.map(i => i && i.price))
    ?? lowestPositive([product.minPrice, product.price])
}

function retailAmount(product) {
  const items = Array.isArray(product.items) ? product.items : []
  return lowestPositive([product.retailPrice])
    ?? lowestPositive(items.map(i => i && i.retailPrice))
}

const KINDS = [
  { kind: 'wholesale', label: 'Wholesale', amount: wholesaleAmount },
  { kind: 'retail', label: 'Retail', amount: retailAmount },
]

/** The price rows one card prints, wholesale first. */
export function productPrices(product, priceVisibility) {
  if (!product) return []
  const visible = visiblePriceKinds(priceVisibility)
  return KINDS
    .filter(k => visible[k.kind])
    .map(k => ({ k, amount: k.amount(product) }))
    .filter(({ amount }) => amount !== null)
    .map(({ k, amount }) => ({ kind: k.kind, label: k.label, text: formatMoney(amount, product.currency) }))
}

/** The facts column's line on which prices the sheet shows. */
export function priceBasisLabel(priceVisibility) {
  const { wholesale, retail } = visiblePriceKinds(priceVisibility)
  if (wholesale && retail) return 'Wholesale and retail prices shown'
  if (wholesale) return 'Wholesale prices shown'
  if (retail) return 'Retail prices shown'
  return 'Prices on request'
}
